import React, { useEffect, useRef } from "react";
import { makeStyles, useTheme, useMediaQuery } from "@material-ui/core";
import DOMPurify from "dompurify";

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    position: "relative",
    // Add overflow control for mobile
    overflow: "hidden",
    // Add padding for mobile to prevent content from touching edges
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  page: {
    width: "8.5in",
    minHeight: "11in",
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    marginBottom: theme.spacing(3),
    padding: "1in",
    boxSizing: "border-box",
    position: "relative",
    overflow: "auto",
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: "12pt",
    lineHeight: 1.5,
    color: "#000",
    // Improved responsive scaling for different screen sizes
    [theme.breakpoints.down(1416)]: {
      width: "100%",
      maxWidth: "8.5in",
      padding: "0.75in",
      transform: "scale(1)",
      transformOrigin: "top center",
    },
    // Enhanced mobile responsiveness
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%", // Allow full width on mobile
      padding: "0.3in", // Even smaller padding for more content space
      fontSize: "12pt", // Maintain readable font size
      lineHeight: 1.4, // Improved line height for readability
      transform: "scale(1)",
      transformOrigin: "top center",
      margin: "0 auto",
      boxShadow: "none", // Remove shadow on mobile for cleaner look
      borderRadius: 0, // Remove border radius on mobile
      // Add touch-specific optimizations
      WebkitTapHighlightColor: "transparent", // Remove tap highlight
      WebkitTouchCallout: "none", // Disable callout
      WebkitUserSelect: "text", // Allow text selection
      userSelect: "text",
      // Improve scrolling
      overscrollBehavior: "none", // Prevent pull-to-refresh
      // Adjust margins for better mobile experience
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        marginTop: "0.4em",
        marginBottom: "0.4em",
        lineHeight: 1.3,
      },
      "& h1": {
        fontSize: "18pt", // Larger heading for mobile
      },
      "& h2": {
        fontSize: "16pt",
      },
      "& h3": {
        fontSize: "14pt",
      },
      "& p": {
        margin: "0.4em 0",
        fontSize: "12pt", // Maintain readable text size
      },
      "& ul, & ol": {
        marginTop: "0.4em",
        marginBottom: "0.4em",
        paddingLeft: "0.8em", // Further reduced indentation on mobile
      },
      "& li": {
        margin: "0.25em 0",
        fontSize: "12pt", // Maintain readable text size
      },
      // Improve touch targets
      "& a": {
        padding: "0.3em 0", // Larger touch target for links
        display: "inline-block", // Allow padding on links
        minHeight: "24px", // Minimum height for better touch targets
      },
      // Improve table display on mobile
      "& table": {
        width: "100%",
        fontSize: "11pt", // Slightly smaller font for tables to fit better
      },
      "& th, & td": {
        padding: "4px", // Smaller padding in table cells
      },
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginTop: "0.5em",
      marginBottom: "0.5em",
      fontWeight: "bold",
    },
    "& h1": {
      fontSize: "18pt",
    },
    "& h2": {
      fontSize: "16pt",
    },
    "& h3": {
      fontSize: "14pt",
    },
    "& p": {
      margin: "0.5em 0",
    },
    "& ul, & ol": {
      marginTop: "0.5em",
      marginBottom: "0.5em",
      paddingLeft: "1.5em",
    },
    "& a": {
      color: "#0000EE",
      textDecoration: "underline",
    },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      marginBottom: "1em",
    },
    "& th, & td": {
      border: "1px solid #ddd",
      padding: "8px",
      textAlign: "left",
    },
    "& th": {
      backgroundColor: "#f2f2f2",
      fontWeight: "bold",
    },
  },
  editable: {
    outline: "none",
    "&:focus": {
      boxShadow: "0 0 0 2px rgba(63, 81, 181, 0.3)",
    },
  },
  pageBreak: {
    width: "100%",
    height: "1px",
    backgroundColor: "#ccc",
    margin: "0.5in 0",
    position: "relative",
    "&::after": {
      content: '"Page Break"',
      position: "absolute",
      top: "-10px",
      right: "0",
      backgroundColor: "#f0f0f0",
      padding: "2px 8px",
      fontSize: "10px",
      color: "#666",
      borderRadius: "4px",
    },
  },
  pageBreakIndicator: {
    position: "absolute",
    left: "0.5in",
    right: "0.5in",
    borderBottom: "2px dashed #ccc",
    pointerEvents: "none", // Ensures it doesn't interfere with editing
    zIndex: 10,
    // Adjust for mobile screens
    [theme.breakpoints.down("sm")]: {
      left: "0.25in",
      right: "0.25in",
    },
  },
  // Enhanced mobile toolbar for better mobile UX
  mobileControls: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(50, 50, 50, 0.98)"
          : "rgba(255, 255, 255, 0.98)",
      padding: theme.spacing(1),
      justifyContent: "space-around",
      alignItems: "center",
      zIndex: 1000,
      boxShadow: "0 -3px 10px rgba(0,0,0,0.2)",
      borderTop: `1px solid ${theme.palette.divider}`,
      backdropFilter: "blur(10px)", // Add blur effect for modern look
      WebkitBackdropFilter: "blur(10px)", // Safari support
    },
  },
  mobileButton: {
    padding: theme.spacing(0.75),
    minWidth: 0,
    borderRadius: "50%",
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(80, 80, 80, 0.9)"
        : "rgba(245, 245, 245, 0.9)",
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    fontSize: "1rem",
    fontWeight: "bold",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    transition: "all 0.2s ease",
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      transform: "scale(0.95)",
    },
    // Add ripple effect
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      width: 0,
      height: 0,
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      borderRadius: "50%",
      transform: "translate(-50%, -50%)",
      opacity: 0,
    },
    "&:active::after": {
      width: "100%",
      height: "100%",
      opacity: 1,
      transition: "width 0.3s ease, height 0.3s ease, opacity 0.3s ease",
    },
  },
  scaleIndicator: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(80, 80, 80, 0.9)"
          : "rgba(245, 245, 245, 0.9)",
      color: theme.palette.text.primary,
      borderRadius: 16,
      padding: theme.spacing(0.5, 1.5),
      fontSize: "0.8rem",
      fontWeight: "bold",
      minWidth: 48,
      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      border: `1px solid ${theme.palette.divider}`,
    },
  },
  // Add a hint overlay for mobile users
  mobileHint: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      padding: theme.spacing(1),
      backgroundColor: "rgba(33, 150, 243, 0.9)",
      color: "#fff",
      fontSize: "0.8rem",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1001,
      opacity: 1,
      transition: "opacity 0.5s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
  },
  mobileHintHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  // Add a reset zoom button
  resetZoomButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

/**
 * DocumentPage Component
 *
 * A component that renders a document page with editable content
 *
 * @param {Object} props - Component props
 * @param {string} props.content - The HTML content to display
 * @param {boolean} props.isEditable - Whether the content is editable
 * @param {boolean} props.isHtml - Whether the content is HTML (true) or Markdown (false)
 * @param {Function} props.onInput - Callback for input events
 * @param {Function} props.onBlur - Callback for blur events
 * @param {Function} props.onClick - Callback for click events
 * @param {Object} props.editorRef - Ref to the editor element
 * @param {boolean} props.showPageBreaks - Whether to show page breaks
 * @param {number} props.pageHeight - Height of a page in pixels
 * @returns {JSX.Element} The rendered component
 */
const DocumentPage = ({
  content,
  isEditable = false,
  isHtml = true,
  onInput,
  onBlur,
  onClick,
  editorRef,
  showPageBreaks = false,
  pageHeight = 1056, // 11in * 96dpi
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const contentRef = useRef(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1416));

  // Store the initial scale factor in a ref to avoid recalculation
  const initialScaleFactorRef = useRef(null);

  // Enhanced scale factor calculation for better mobile experience
  const getScaleFactor = () => {
    if (!isSmallScreen) return 1; // No scaling needed for large screens

    // For screens smaller than 1416px, calculate a scale factor
    // that ensures the document fits within the available width
    // We use a base width of 816px (8.5in) and adjust based on screen size
    const containerWidth = window.innerWidth - 48; // Subtract padding
    const documentWidth = 816; // 8.5in in pixels

    // For very small screens (mobile), use a different approach
    if (window.innerWidth < 600) {
      // On mobile, we want to fit the width completely while maintaining aspect ratio
      // Use a more aggressive scaling to avoid the need for manual pinch-zoom
      const mobileScale = (window.innerWidth - 24) / documentWidth;
      // Use a lower minimum scale to ensure the document fits better on mobile
      return Math.max(mobileScale, 0.35);
    }

    // For tablets and larger screens, use the original calculation
    // but with a slightly higher minimum to ensure readability
    return Math.max(containerWidth / documentWidth, 0.65);
  };

  // Using refs to track content and editing state
  const isFirstContentUpdateRef = useRef(true);
  const prevContentRef = useRef("");
  const isEditingRef = useRef(false);

  // Determine if the device is mobile or tablet
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = isSmallScreen && !isMobile;

  useEffect(() => {
    if (contentRef.current && editorRef && content) {
      const contentChanged = content !== prevContentRef.current;
      const shouldUpdateContent =
        isFirstContentUpdateRef.current ||
        !isEditable ||
        (contentChanged && !isEditingRef.current);

      if (shouldUpdateContent) {
        console.log("Updating document content");
        contentRef.current.innerHTML = DOMPurify.sanitize(content);
        isFirstContentUpdateRef.current = false;
        prevContentRef.current = content;
        if (editorRef) {
          editorRef.current = contentRef.current;
        }
      }
    }
  }, [content, editorRef, isEditable]);

  // Set initial scale factor on first render
  useEffect(() => {
    if (initialScaleFactorRef.current === null) {
      initialScaleFactorRef.current = getScaleFactor(); // Calculate and store
      // For mobile, CSS handles initial scale via `transform: scale(1)`.
      // For tablets, the scale is applied via pageStyle.
      // No explicit JS initial transform needed here if CSS handles mobile.
      if (isMobile) {
        console.log(
          "Mobile detected, CSS will handle initial scale via classes."
        );
      }
    }
  }, [isMobile]); // Rerun if isMobile changes (e.g. orientation change if it affects breakpoint)

  // Apply dynamic scaling based on screen size
  const scaleFactor = initialScaleFactorRef.current || getScaleFactor();

  const pageStyle = {};
  if (isTablet) {
    // Apply JS scaling only for tablets
    pageStyle.transformOrigin = "top center";
    pageStyle.transform = `scale(${scaleFactor})`;
    pageStyle.width = "8.5in";
    pageStyle.maxWidth = "none"; // Override CSS maxWidth for tablets
  }
  // For mobile (isMobile = true), pageStyle remains empty, CSS rules from useStyles will apply.
  // This includes width: 100%, maxWidth: 100%, and transform: scale(1).
  // For large screens (isSmallScreen = false), pageStyle also remains empty, default CSS applies.

  // Get current scale from element
  const getCurrentScale = () => {
    if (!contentRef.current) return scaleFactor;

    const transform = contentRef.current.style.transform;
    if (!transform || !transform.includes("scale")) return scaleFactor;

    const match = transform.match(/scale\(([^)]+)\)/);
    return match ? parseFloat(match[1]) : scaleFactor;
  };

  // Add a function to handle mobile zoom
  const handleMobileZoomIn = () => {
    if (contentRef.current) {
      const currentScale = getCurrentScale();
      contentRef.current.style.transform = `scale(${Math.min(
        currentScale + 0.1,
        1.5
      )})`;
    }
  };

  const handleMobileZoomOut = () => {
    if (contentRef.current) {
      const currentScale = getCurrentScale();
      contentRef.current.style.transform = `scale(${Math.max(
        currentScale - 0.1,
        0.3
      )})`;
    }
  };

  // Add a function to reset zoom to optimal level
  const handleResetZoom = () => {
    if (contentRef.current) {
      // Calculate optimal zoom level for current screen
      const optimalScale = getScaleFactor() * 0.85; // Slightly zoomed out for better view
      contentRef.current.style.transform = `scale(${optimalScale})`;
    }
  };

  // Track when user is actively editing
  const handleInput = (e) => {
    isEditingRef.current = true;
    if (onInput) {
      onInput(e);
    }
  };

  // Reset editing state on blur
  const handleBlur = (e) => {
    isEditingRef.current = false;
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className={classes.pageContainer}>
      <div
        className={`${classes.page} ${isEditable ? classes.editable : ""}`}
        ref={contentRef}
        contentEditable={isEditable}
        suppressContentEditableWarning={true}
        onInput={handleInput}
        onBlur={handleBlur}
        onClick={onClick}
        data-testid="document-page"
        style={pageStyle}
      />
      {/* Add a subtle page break indicator at the standard page break position */}
      <div
        className={classes.pageBreakIndicator}
        style={{
          top: "14.85in", // Position at 10.5 inches (standard letter page is 11in, leaving 0.5in margin)
          height: "1px",
        }}
        data-html-to-docx-exclude="true" // This attribute ensures it won't be included in the Word doc
      />

      {/* Mobile hint that disappears after 5 seconds */}
      {isMobile && (
        <div
          className={`${classes.mobileHint} ${
            isFirstContentUpdateRef.current ? "" : classes.mobileHintHidden
          }`}
          ref={(el) => {
            if (el && isFirstContentUpdateRef.current) {
              setTimeout(() => {
                el.classList.add(classes.mobileHintHidden);
              }, 5000);
            }
          }}
        >
          Tap to edit • Use toolbar below for formatting
        </div>
      )}
    </div>
  );
};

export default DocumentPage;
